import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { PracticeChallenge } from "@/components/PracticeChallenge";
import { Insight } from "@/components/Insight";
import { Keyboard } from "@/components/Keyboard";
import {
  Tip, KeyIdea, Pull, Steps, Step, Vocab, Term, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation8() {
  const { lesson, course, accent } = findLesson("piano-foundation", "8");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight>Stop thinking in <strong>letter names</strong>. Start thinking in <strong>scale-degree numbers</strong>: 1, 2, 3, 4, 5, 6, 7, 1′ (octave) — or in swaras: Sa, Re, Ga, Ma, Pa, Dha, Ni, Sa.</Highlight>
        <Highlight>Twinkle Twinkle has a fixed shape: <strong>1-1-5-5-6-6-5</strong> · <strong>4-4-3-3-2-2-1</strong>. The numbers stay the same in every key — only the note names change.</Highlight>
        <Highlight>Once the shape is in numbers, you can play it on every white-note scale: A, B, C, D, E, F, G major.</Highlight>
        <Highlight>The C-major-is-easy, black-keys-are-scary mindset is the <em>most ridiculous</em> place to start. Guitar players love E. Trumpet players love B♭. Singers pick whichever key fits their voice.</Highlight>
        <Highlight>Practise this anywhere — even without a piano. Use a free app (GarageBand, Perfect Piano), tap on a surface, or just sing the numbers in your head.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="The big shift" title="From letter names to scale degrees" accent={accent.hex}>
        <p>
          Any earworm — a nursery rhyme from school, a favourite pop song, a film tune you
          can't shake — is a sequence of <strong>intervals</strong>. Intervals are the
          building blocks of melody. Once you analyse a melody as <em>scale degrees</em>{" "}
          rather than as letter names, that melody becomes portable: it lives in any key.
        </p>

        <Vocab>
          <Term name="Scale degree" def="a note's position in the scale, by number — 1, 2, 3, 4, 5, 6, 7, octave (1′)." />
          <Term name="Swara" def="the Indian name for a scale degree — Sa, Re, Ga, Ma, Pa, Dha, Ni, Sa. Beautiful to sing." />
          <Term name="Interval" def="the distance between two notes, measured in scale degrees." />
          <Term name="Transposition" def="moving the same melody to a new starting note. The numbers don't change; the letter names do." />
        </Vocab>

        <KeyIdea>
          The numbers ARE the melody. Letters are just one of many ways to spell it. Twinkle
          Twinkle in C uses the same numbers as Twinkle Twinkle in D — only the keys under
          your fingers change.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="The chart you're going to build" title="Scale degrees · swaras · letter names · stacked" accent={accent.hex}>
        <p>
          The chart below pairs the rows you'll want to write down. For each scale, line up three rows:
          the scale-degree numbers, the swaras, and the letter names of that particular scale.
        </p>

        <div className="overflow-x-auto rounded-xl border border-nsm-border bg-nsm-card/50 p-4 text-[13px]">
          <table className="w-full">
            <thead>
              <tr className="text-nsm-dim">
                <th className="text-left">Scale degree</th>
                <th>1</th><th>2</th><th>3</th><th>4</th><th>5</th><th>6</th><th>7</th><th>1′</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-nsm-border">
                <td className="py-1 text-amber-300">Swara</td>
                <td>Sa</td><td>Re</td><td>Ga</td><td>Ma</td><td>Pa</td><td>Dha</td><td>Ni</td><td>Sa</td>
              </tr>
              <tr className="border-t border-nsm-border">
                <td className="py-1 text-emerald-300">D major</td>
                <td>D</td><td>E</td><td>F♯</td><td>G</td><td>A</td><td>B</td><td>C♯</td><td>D</td>
              </tr>
              <tr className="border-t border-nsm-border">
                <td className="py-1 text-blue-300">C major</td>
                <td>C</td><td>D</td><td>E</td><td>F</td><td>G</td><td>A</td><td>B</td><td>C</td>
              </tr>
              <tr className="border-t border-nsm-border">
                <td className="py-1 text-rose-300">F major</td>
                <td>F</td><td>G</td><td>A</td><td>B♭</td><td>C</td><td>D</td><td>E</td><td>F</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Insight>

      <Insight eyebrow="Twinkle Twinkle as numbers" title="The same melody, multiple scales" accent={accent.hex}>
        <p>
          The first demo is everyone's favourite — Twinkle Twinkle Little Star.
          Written as scale-degree numbers it looks like this:
        </p>

        <div className="rounded-xl border border-nsm-border bg-zinc-900/40 p-4 font-mono text-[14px]">
          <div className="text-amber-300">Line 1 · 1 1 5 5 6 6 5</div>
          <div className="text-amber-300">Line 2 · 4 4 3 3 2 2 1</div>
          <div className="mt-3 text-nsm-dim text-[12px]">(continues with two more lines, finishing the song — you finish that as homework)</div>
        </div>

        <p className="mt-4">
          Drop those numbers onto <strong>D major</strong>:{" "}
          <span className="font-mono text-emerald-300">D D A A B B A · G G F♯ F♯ E E D</span>.
          Now drop them onto <strong>C major</strong>:{" "}
          <span className="font-mono text-blue-300">C C G G A A G · F F E E D D C</span>.
          Same numbers, two different scales — same Twinkle Twinkle in both.
        </p>

        <Keyboard
          startOctave={3}
          octaves={2}
          highlights={[
            { pitch: "C4", color: "#3b82f6", label: "1" },
            { pitch: "G4", color: "#3b82f6", label: "5" },
            { pitch: "A4", color: "#ec4899", label: "6" },
            { pitch: "F4", color: "#3b82f6", label: "4" },
            { pitch: "E4", color: "#3b82f6", label: "3" },
            { pitch: "D4", color: "#3b82f6", label: "2" },
          ]}
          height={170}
        />

        <PracticeChallenge
          courseSlug="piano-foundation"
          order="8"
          id="twinkle"
          mode="sequence"
          title="Twinkle in C major"
          prompt="Play Twinkle by its shape — 1-1-5-5-6-6-5, 4-4-3-3-2-2-1. In C that's C C G G A A G, F F E E D D C."
          notes={["C4", "C4", "G4", "G4", "A4", "A4", "G4", "F4", "F4", "E4", "E4", "D4", "D4", "C4"]}
          startOctave={3}
          octaves={2}
        />
      </Insight>

      <Insight eyebrow="Happy Birthday too" title="Same trick, second tune" accent={accent.hex}>
        <p>
          The second demo: <strong>Happy Birthday</strong>. The opening phrase as
          scale-degrees is <strong>5 5 6 5 1′ 7</strong>. He plays it in F major first
          (5=C, 6=D, 1′=F, 7=E), then transposes to G major (5=D, 6=E, 1′=G, 7=F♯). The
          numbers don't change. Only the keys do.
        </p>

        <Pull>
          If you've done it right, it's going to sound exactly like Happy Birthday. If you've
          done it wrong, it won't. There's no middle ground.
        </Pull>
      </Insight>

      <Insight eyebrow="Why all seven natural scales" title="The 'C is easy' myth" accent={accent.hex}>
        <Pull>
          A lot of newcomer pianists who maybe start with YouTube and standard systems
          are always under this impression that C major is easy and C major is the scale
          I have to start with and black notes are scary. I think that is the most
          ridiculous thing to start with or at any point in your piano learning journey.
        </Pull>

        <p>
          The goal is fluency in <strong>all seven natural scales</strong> —
          A, B, C, D, E, F, G major (every scale that starts on a white note). His reasoning
          is practical:
        </p>
        <ul className="space-y-1 text-[14px]">
          <li><strong>Guitar players love E.</strong> If you jam with one, you'll need it.</li>
          <li><strong>Trumpet players love B♭.</strong></li>
          <li><strong>Saxophone players love E♭.</strong></li>
          <li><strong>Singers</strong> pick whichever scale fits their voice — and that's never just C.</li>
        </ul>
        <p>
          Insisting on C major locks you out of half the people you'd want to play music with.
          Drilling Twinkle Twinkle and Happy Birthday across all seven naturals is the cure.
        </p>
      </Insight>

      <Insight eyebrow="Practice path" title="Numbers · sing · play · transpose" accent={accent.hex}>
        <Steps>
          <Step>
            <strong>Write the chart.</strong> 1 to 8 across the top, swaras under, the scale's
            letter names under that. Pick D major as your first row (the choice here).
          </Step>
          <Step>
            <strong>Numbers + swaras first.</strong> Sing Twinkle Twinkle as numbers
            (one-one-five-five…) or as swaras (Sa-Sa-Pa-Pa-Dha-Dha-Pa). No piano yet.
          </Step>
          <Step>
            <strong>Play it on D major.</strong> Drop the numbers onto the D scale. Sing along
            as you play.
          </Step>
          <Step>
            <strong>Move to C major.</strong> Same numbers, different scale row. Then F major.
            Then G. Until all seven white-note scales feel equally comfortable.
          </Step>
          <Step>
            <strong>Switch tunes.</strong> Try Happy Birthday using its 5-5-6-5-1′-7 opening,
            same multi-scale routine. Pick another song you know — work it out in numbers, then
            play it across keys.
          </Step>
        </Steps>

        <Tip>
          You don't need a real piano for this. A free phone app — GarageBand on iOS or
          Perfect Piano on Android — gives you a virtual keyboard. Number-fluency is the goal;
          the instrument is interchangeable.
        </Tip>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-9"
        label="Lesson 9 · Expansion-Compression Exercise"
        why="Now that the hand is moving across scales, the next drill stretches the hand beyond its five-finger comfort zone — expanding outward and compressing back."
      />
    </LessonShell>
  );
}
